import { config } from "config";

import { SubscriptionPhase } from "constants/subscription-phase";
import { getItem } from "services/session-storage";
import { IAgent } from "store/entities/agents";
import { IBilling } from "store/entities/billing";
import { ILicense, ILicenseSourceParams } from "store/entities/license";
import { ISubscription } from "store/entities/subscriptions";

import { installAmplitude } from "./amplitude";
import { isInIframe } from "./app";
import { getSubscriptionPhase, inTrial } from "./subscription";
import { getDaysLeft } from "./subscription-plans";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any;

export const SESSION_STORAGE_SESSION_SOURCE_PARAMS_KEY = "session_source";

export enum EventPlace {
  AccountDetails = "Account Details",
  Agents = "Agents",
  AgentDetails = "Agent details",
  CustomViews = "Custom views",
  Archives = "Archives",
  Chats = "Chats",
  DevInstructions = "Dev instructions",
  InstallCode = "Install code",
  Invoices = "Invoices",
  Marketplace = "Marketplace",
  Onboarding = "Onboarding",
  Teams = "Teams",
  ProfileMenu = "Profile menu",
  Reports = "Reports",
  Settings = "Settings",
  SettingsCustomization = "Settings - Customization",
  Subscription = "Subscription",
  Tickets = "Tickets",
  WelcomeTutorial = "Welcome tutorial",
  Navbar = "Navbar",
  BulkActions = "Bulk actions",
  MoreOptions = "More options",
  TicketTopLink = "Top link",
  TicketBottomButton = "Bottom button",
  TicketDetails = "Ticket details",
  RichTextEditor = "RichTextEditor",
  AppDetails = "App details",
  AppList = "App list",
  VisualBuilder = "Visual builder",
  LiveChat = "LiveChat",
  Automations = "Automations",
  TicketToolbar = "Ticket toolbar",
  TicketRating = "Ticket rating",
}

// FIXME: Fix this the next time the file is edited.
// @ts-expect-error TS(6133): 'place' is declared but its value is never read.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function trackEngagement(action: string, place?: string, properties?: Record<string, unknown>) {
  if (window.amplitude) {
    const sourceParams = getItem(SESSION_STORAGE_SESSION_SOURCE_PARAMS_KEY);
    const propertiesWithSourceParams = {
      ...properties,
      ...sourceParams,
    };
    window.amplitude.getInstance().logEvent(action, propertiesWithSourceParams);


    if (["labs", "dev"].includes(config.env)) {
      const consoleLogArgs = [`[Amplitude] ${action}`];
      if (propertiesWithSourceParams) {
        consoleLogArgs.push(propertiesWithSourceParams);
      }
      // eslint-disable-next-line no-console
      console.log.apply(null, consoleLogArgs);
    }

    if (action.startsWith("Welcome tutorial")) {
      pushDataLayerEvent(action);
    }
  }
}

export const trackEngagementWaitForAmplitude = async (
  action: string,
  place?: string,
  properties?: Record<string, unknown>
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
) => {
  return new Promise<void>(resolve => {
    const intervalId = setInterval(() => {
      if (window.amplitude) {
        clearInterval(intervalId);
        trackEngagement(action, place, properties);
        resolve();
      }
    }, 100);
  });
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-empty-object-type
export function trackGTMEvent(event: string, eventDetails?: {}) {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataLayer = (window as any).dataLayer || [];
  dataLayer.push({ event, ...eventDetails });
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
export function trackGAEvent(action: string, opts?: any) {
  if (window.ga) {
    window.ga(action, opts);
  }
}
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function installAmplitudeWithUserData(
  currentAgent: IAgent,
  licenseId: number,
  licenseSource: ILicenseSourceParams,
  currentSubscription: ISubscription | null,
  currentBilling: IBilling | null
) {
  if (!window.amplitude) {
    installAmplitude();
  }
  window.amplitude.getInstance().init(config.amplitudeKey, currentAgent?.id);

  const userProperties: Record<string, unknown> = {
    email: currentAgent?.email,
    licenseId,
    role: currentAgent?.permission,
    ...licenseSource,
  };

  if (currentSubscription) {
    userProperties.subscriptionPlan = currentSubscription.plan;
    userProperties.subscriptionAgents = currentSubscription.agents;
    userProperties.subscriptionPhase = getSubscriptionPhase(currentSubscription, currentBilling);
  }

  window.amplitude.getInstance().setUserProperties(userProperties);
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function installAmplitudeNoAuth() {
  if (!window.amplitude) {
    installAmplitude();
  }
  window.amplitude.getInstance().init(config.amplitudeKey);
}

const subscriptionPhasesTrial = [SubscriptionPhase.TrialCC, SubscriptionPhase.TrialNoCC];
const subscriptionPhasesPaid = [SubscriptionPhase.Paid, SubscriptionPhase.PaidNoCC];

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function installGTM(
  currentAgent: IAgent,
  licenseId: number,
  currentSubscription: ISubscription | null,
  currentBilling: IBilling | null,
  isDefaultMailboxUsed: boolean
) {
  /* eslint-disable */
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });
    const f = d.getElementsByTagName(s)[0];
    const j: any = d.createElement(s);
    const dl = l != "dataLayer" ? `&l=${l}` : "";
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
    // @ts-expect-error TS(2531): Object is possibly 'null'.
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", "GTM-WS62QGR");
  /* eslint-enable */

  const subscriptionPhase = getSubscriptionPhase(currentSubscription, currentBilling);
  let daysLeft = null;
  if (subscriptionPhasesTrial.includes(subscriptionPhase)) {
    daysLeft = getDaysLeft(currentSubscription);
  } else if (subscriptionPhasesPaid.includes(subscriptionPhase)) {
    daysLeft = 0;
  }

  window.dataLayer.push({
    event: "custom_dimensions",
    license: licenseId,
    licenseType: getSubscriptionPhase(currentSubscription, currentBilling),
    daysLeft,
    forwardingDetected: isDefaultMailboxUsed,
    plan: currentSubscription?.plan,
    role: currentAgent?.permission,
  });
}

export function pushDataLayerEvent(eventName: string): void {
  window.dataLayer?.push({ event: eventName });
}

export interface IAnalyticsIdentity {
  user_firstname: string;
  user_id: string;
  user_email: string;
  user_role: string;
  license_id: number;
  company_name: string;
  subscription_plan: string;
  subscription_status: string;
  subscription_phase: string;
  has_livechat: boolean;
  has_knowledgebase: boolean;
  production: boolean;
  utm_source: string;
  iframe: boolean;
  is_business_email: boolean;
}

export function identify(
  agent: IAgent,
  license: ILicense,
  subscription: ISubscription,
  hasLivechat: boolean,
  hasKnowledgebase: boolean,
  isBusinessEmail: boolean
): IAnalyticsIdentity {
  return {
    user_firstname: agent?.name,
    user_id: agent?.id,
    user_email: agent?.email,
    user_role: agent?.permission,
    license_id: license?.id,
    company_name: license?.settings.companyName,
    subscription_plan: subscription?.plan,
    subscription_status: subscription?.status,
    subscription_phase: inTrial(subscription) ? "trial" : "paid",
    has_livechat: hasLivechat,
    has_knowledgebase: hasKnowledgebase,
    production: config.env === "production",
    // @ts-expect-error TS2322
    utm_source: license.source.utmSource || null,
    iframe: isInIframe(),
    is_business_email: isBusinessEmail,
  };
}
